/*
 This file is part of GNU Taler
 (C) 2021-2024 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.

 GNU Taler is distributed in the hope that it will be useful, but WITHOUT ANY
 WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 A PARTICULAR PURPOSE.  See the GNU General Public License for more details.

 You should have received a copy of the GNU General Public License along with
 GNU Taler; see the file COPYING.  If not, see <http://www.gnu.org/licenses/>
 */

/**
 *
 * @author Sebastian Javier Marchano (sebasjm)
 */

import {
  AbsoluteTime,
  HttpStatusCode,
  TalerError,
  TalerErrorCode,
  TalerMerchantApi,
  assertUnreachable,
} from "@gnu-taler/taler-util";
import {
  LocalNotificationBannerBulma,
  useLocalNotificationBetter,
  useTranslationContext,
} from "@gnu-taler/web-util/browser";
import { format } from "date-fns";
import { VNode, h } from "preact";
import { useState } from "preact/hooks";
import { ErrorLoadingMerchant } from "../../../../components/ErrorLoadingMerchant.js";
import { Loading } from "../../../../components/exception/loading.js";
import { JumpToElementById } from "../../../../components/form/JumpToElementById.js";
import { DatePicker } from "../../../../components/picker/DatePicker.js";
import { Tooltip } from "../../../../components/Tooltip.js";
import { useSessionContext } from "../../../../context/session.js";
import {
  useInstanceOrders,
  useOrderDetails
} from "../../../../hooks/order.js";
import {
  dateFormatForPreferences,
  usePreference,
} from "../../../../hooks/preference.js";
import { LoginPage } from "../../../login/index.js";
import { NotFoundPageOrAdminCreate } from "../../../notfound/index.js";
import { ListPage } from "./ListPage.js";
import { RefundModal } from "./Table.js";

const TALER_SCREEN_ID = 46;

interface Props {
  onSelect: (id: string) => void;
  onChangeSection: (s: OrderListSection) => void;
  onCreate: () => void;
  section: OrderListSection;
}

export enum OrderListSection {
  NEW = "new", // paid = false
  PAID = "paid", // paid = true
  REFUNDED = "refunded", // refunded = true
  PENDING = "pending", // paid = true, wired = false
  INCOMING = "incoming", // wired = true,
  SETTLED = "settled", // wired = true,
  ALL = "all", //
}

function sectionToFilter(s?: OrderListSection): {
  paid?: boolean;
  wired?: boolean;
  refunded?: boolean;
} {
  switch (s) {
    case OrderListSection.NEW:
      return { paid: false };
    case OrderListSection.PAID:
      return { paid: true };
    case OrderListSection.REFUNDED:
      return { refunded: true };
    case OrderListSection.PENDING:
      return { paid: true, wired: false };
    case OrderListSection.INCOMING:
      return { wired: true };
    case OrderListSection.SETTLED:
    case OrderListSection.ALL:
    case undefined:
      return {};
    default:
      assertUnreachable(s);
  }
}

export default function OrderList({
  onCreate,
  onSelect,
  onChangeSection,
  section,
}: Props): VNode {
  const [filter, setFilter] = useState<{
    date?: AbsoluteTime;
    position?: string;
  }>({});

  const [orderToBeRefunded, setOrderToBeRefunded] = useState<
    TalerMerchantApi.OrderHistoryEntry | undefined
  >(undefined);

  const setNewDate = (date?: AbsoluteTime): void =>
    setFilter((prev) => ({ ...prev, date }));

  const result = useInstanceOrders(
    { ...sectionToFilter(section), ...filter },
    (d) => setFilter({ ...filter, position: d }),
  );
  const [notification, safeFunctionHandler] = useLocalNotificationBetter();
  const { state: session, lib } = useSessionContext();

  const { i18n } = useTranslationContext();

  if (!result) return <Loading />;
  if (result instanceof TalerError) {
    return <ErrorLoadingMerchant error={result} />;
  }
  if (result.type === "fail") {
    switch (result.case) {
      case HttpStatusCode.NotFound: {
        return <NotFoundPageOrAdminCreate />;
      }
      case HttpStatusCode.Unauthorized: {
        return <LoginPage focus />;
      }
      default: {
        assertUnreachable(result);
      }
    }
  }

  const data = {} as TalerMerchantApi.RefundRequest;
  const refund = safeFunctionHandler(
    i18n.str`authorize refund`,
    lib.instance.addRefund.bind(lib.instance),
    !session.token || !orderToBeRefunded
      ? undefined
      : [session.token, orderToBeRefunded.order_id, data],
  );
  refund.onSuccess = () => {
    setOrderToBeRefunded(undefined);
  };
  refund.onFail = (fail) => {
    switch (fail.case) {
      case HttpStatusCode.Unauthorized:
        return i18n.str`Unauthorized.`;
      case HttpStatusCode.Forbidden:
        return i18n.str`Forbidden.`;
      case HttpStatusCode.NotFound:
        return i18n.str`Not found.`;
      case HttpStatusCode.Conflict:
        return i18n.str`Conflict.`;
      case HttpStatusCode.Gone:
        return i18n.str`Gone.`;
      case HttpStatusCode.UnavailableForLegalReasons:
        return i18n.str`UnavailableForLegalReasons.`;
      default:
        assertUnreachable(fail);
    }
  };
  const [pickDate, setPickDate] = useState(false);
  const [preferences] = usePreference();

  return (
    <section class="section is-main-section">
      <LocalNotificationBannerBulma notification={notification} />

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          width: "100%",
        }}
      >
        <JumpToElementById
          onSelect={onSelect}
          description={i18n.str`Jump to order with the given product ID`}
          placeholder={i18n.str`Order id`}
        />

        <div class="buttons is-right">
          <div class="field has-addons">
            {filter.date && (
              <div class="control">
                <a
                  class="button is-fullwidth"
                  onClick={() => setNewDate(undefined)}
                >
                  <Tooltip text={i18n.str`Clear date filter`}>
                    <i class="icon mdi mdi-close" />
                  </Tooltip>
                </a>
              </div>
            )}
            <div class="control">
              <Tooltip text={i18n.str`Select date to show nearby orders`}>
                <input
                  class="input"
                  type="text"
                  readonly
                  value={
                    !filter.date || filter.date.t_ms === "never"
                      ? ""
                      : format(
                          filter.date.t_ms,
                          dateFormatForPreferences(preferences),
                        )
                  }
                  placeholder={i18n.str`Jump to date (${dateFormatForPreferences(
                    preferences,
                  )})`}
                  onClick={() => {
                    setPickDate(true);
                  }}
                />
              </Tooltip>
            </div>
            <div class="control">
              <Tooltip text={i18n.str`Select date to show nearby orders`}>
                <a
                  class="button  is-fullwidth"
                  onClick={() => {
                    setPickDate(true);
                  }}
                >
                  <i class="icon mdi mdi-calendar" />
                </a>
              </Tooltip>
            </div>
          </div>
        </div>
      </div>
      <DatePicker
        opened={pickDate}
        closeFunction={() => setPickDate(false)}
        dateReceiver={(d) => {
          setNewDate(AbsoluteTime.fromMilliseconds(d.getTime()));
        }}
      />
      <ListPage
        orders={result.body.map((o) => ({ ...o, id: o.order_id }))}
        onLoadMoreBefore={result.loadFirst}
        onLoadMoreAfter={result.loadNext}
        onSelectOrder={(order) => onSelect(order.id)}
        onRefundOrder={(value) => setOrderToBeRefunded(value)}
        section={section}
        onCreate={onCreate}
        onChangeSection={onChangeSection}
      />

      {orderToBeRefunded && (
        <RefundModalForTable
          id={orderToBeRefunded.order_id}
          onCancel={() => setOrderToBeRefunded(undefined)}
          onConfirmed={() => setOrderToBeRefunded(undefined)}
        />
      )}
    </section>
  );
}

interface RefundProps {
  id: string;
  onCancel: () => void;
  onConfirmed: () => void;
}

function RefundModalForTable({
  id,
  onConfirmed,
  onCancel,
}: RefundProps): VNode {
  const { i18n } = useTranslationContext();
  const result = useOrderDetails(id);
  if (!result) return <Loading />;
  if (result instanceof TalerError) {
    return <ErrorLoadingMerchant error={result} />;
  }
  if (result.type === "fail") {
    switch (result.case) {
      case TalerErrorCode.MERCHANT_GENERIC_INSTANCE_UNKNOWN: {
        return <i18n.Translate>Instance unknown</i18n.Translate>;
      }
      case TalerErrorCode.MERCHANT_GENERIC_ORDER_UNKNOWN: {
        return <i18n.Translate>Order unknown</i18n.Translate>;
      }
      case HttpStatusCode.Unauthorized: {
        return <LoginPage focus />;
      }
      default: {
        assertUnreachable(result);
      }
    }
  }

  return (
    <RefundModal
      id={id}
      order={result.body}
      onCancel={onCancel}
      onConfirmed={onConfirmed}
    />
  );
}
